import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject
} from '@angular/core';
import { QuickSourceProgress } from 'src/app/classes/models/quick-source-progress';
import { QuickSourceProgressName } from 'src/app/interfaces/quick-source-progress-name';
import { ProposalRequestService } from 'src/app/services/proposal-request.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-quicksource-tracker',
  templateUrl: './quicksource-tracker.component.html',
  styleUrl: './quicksource-tracker.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QuicksourceTrackerComponent {
  private _utilityService = inject(UtilityService);
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ToastService: ToastService,
    private ProposalRequestService: ProposalRequestService,
    private SharedDataService: SharedDataService
  ) {
    this.SharedDataService.listenForEvent<QuickSourceProgress>().subscribe({
      next: ({ eventName, data }) => {
        if (
          eventName === 'SetQuickSourceProgress' &&
          data instanceof QuickSourceProgress
        ) {
          const { name } = data;

          if (!name) {
            throw this.ToastService.addErrorToast(
              'Error',
              'QuickSource progress is missing name.',
              { returnError: true }
            );
          }

          this.QuickSourceProgressMap.set(name, data);
          this._cdr.markForCheck();
        }
      }
    });
  }

  @Input() ProposalRequestId?: number;
  @Output() stepChangeEvent = new EventEmitter<QuickSourceProgressName>();

  // QuickSource Progress records mapped by name.
  @Input() QuickSourceProgressMap = new Map<
    QuickSourceProgressName,
    QuickSourceProgress | null
  >([
    ['nameSite', null],
    ['locationContact', null],
    ['datePricingInstructions', null],
    ['vendorSelection', null],
    ['review', null]
  ]);

  QuickSourceProgressNameToLabelMap = new Map<QuickSourceProgressName, string>([
    ['nameSite', 'Bid Name & Site'],
    ['locationContact', 'Location & Contact Information'],
    ['datePricingInstructions', 'Dates, Pricing, & Instructions'],
    ['vendorSelection', 'Add Vendors to Bid'],
    ['review', 'Review Bid']
  ]);

  QuickSourceProgressStatusToClassMap = new Map<
    'Started' | 'Complete',
    'started' | 'complete'
  >([
    ['Started', 'started'],
    ['Complete', 'complete']
  ]);

  preserveMapOrder = this._utilityService.keyValuePairDefaultOrder;

  goToStep(QuickSourceProgress: QuickSourceProgress) {
    const { name } = QuickSourceProgress;

    if (!name) {
      throw this.ToastService.addErrorToast(
        'Error',
        'QuickSource Progress is missing name.',
        { returnError: true }
      );
    }

    this.stepChangeEvent.emit(name);
  }

  getQuickSourceProgressText(
    name: QuickSourceProgressName,
    QuickSourceProgress: QuickSourceProgress | null
  ) {
    const TextParts: string[] = [];

    const quickSourceProgressLabel =
      this.QuickSourceProgressNameToLabelMap.get(name);

    if (!quickSourceProgressLabel) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing label for QuickSource progress.',
        { returnError: true }
      );
    }

    TextParts.push(quickSourceProgressLabel, '-');

    if (QuickSourceProgress) {
      const { status } = QuickSourceProgress;

      if (status) {
        TextParts.push(status);
      } else {
        TextParts.push('Not Started');
      }
    } else {
      TextParts.push('Not Started');
    }

    return TextParts.join(' ');
  }

  getQuickSourceProgressClass(QuickSourceProgress: QuickSourceProgress | null) {
    const classes: string[] = [];
    if (!QuickSourceProgress) {
      classes.push('not-started');
      return classes.join(' ');
    }

    const { status } = QuickSourceProgress;

    if (!status) {
      classes.push('not-started');
      return classes.join(' ');
    }

    const statusClass = this.QuickSourceProgressStatusToClassMap.get(status);

    if (!statusClass) {
      throw new Error('Missing status class');
    }

    classes.push(statusClass);

    if (['Started', 'Complete'].includes(status)) {
      classes.push('selection');
    }

    return classes.join(' ');
  }

  ngOnInit(): void {
    if (this.ProposalRequestId) {
      this.ProposalRequestService.getProposalRequest({
        id: this.ProposalRequestId,
        QuickSourceProgress: JSON.stringify({})
      }).subscribe({
        next: (ProposalRequest) => {
          const { QuickSourceProgresses } = ProposalRequest;

          if (QuickSourceProgresses) {
            QuickSourceProgresses.forEach((QuickSourceProgress) => {
              const { name } = QuickSourceProgress;

              if (!name) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'QuickSource progress is missing name.',
                  { returnError: true }
                );
              }

              this.QuickSourceProgressMap.set(name, QuickSourceProgress);
            });
            this._cdr.markForCheck();
          }
        }
      });
    }
  }
}
